import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FiMail } from 'react-icons/fi';
import { Screen } from '../../components/ui/Screen';
import { Card } from '../../components/ui/Card';
import { Input } from '../../components/ui/Input';
import { Button } from '../../components/ui/Button';
import { sendPasswordReset } from '../../services/firebase/auth.service';
import { useAppContext } from '../../store/AppContext';
import type { FormEvent } from 'react';

export function ForgotPasswordScreen() {
  const navigate = useNavigate();
  const { user, loading: userLoading } = useAppContext();
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  // Redirect if already logged in
  useEffect(() => {
    // Auto-scroll to top when component mounts
    window.scrollTo({ top: 0, behavior: 'smooth' });
    
    if (!userLoading && user) {
      // If email is verified, go to home
      if (user.emailVerified) {
        navigate('/home', { replace: true });
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        // If email is not verified, redirect to verify-email screen
        navigate('/auth/verify-email', { replace: true });
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }
  }, [user, userLoading, navigate]);

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError('');
    setSuccess(false);
    setLoading(true);

    if (!email) {
      setError('Please enter your email address.');
      setLoading(false);
      return;
    }

    try {
      await sendPasswordReset(email);
      setSuccess(true);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to send reset email. Please try again.';
      // Make Firebase errors more user-friendly
      if (message.includes('auth/user-not-found')) {
        setError('No account found with this email address.');
      } else if (message.includes('auth/invalid-email')) {
        setError('Please enter a valid email address.');
      } else {
        setError(message);
      }
    } finally {
      setLoading(false);
    }
  }

  // Show loading state while checking auth
  if (userLoading) {
    return (
      <Screen title="Loading..." subtitle="Checking authentication...">
        <Card>
          <p className="text-center text-gray-500">Loading...</p>
        </Card>
      </Screen>
    );
  }

  // Don't render forgot password form if user is already logged in (will redirect)
  if (user) {
    return null;
  }

  return (
    <Screen
      title="Reset password"
      subtitle="Enter your email and we'll send you a reset link."
    >
      <Card>
        {success ? (
          <div className="space-y-4">
            <div className="rounded-lg bg-gray-50 p-4 text-center border border-gray-200">
              <p className="text-sm font-medium text-gray-900">
                Password reset email sent!
              </p>
              <p className="mt-1 text-xs text-gray-700">
                Check your inbox at <strong>{email}</strong> and follow the link to reset your password.
              </p>
            </div>
            <div className="text-center text-xs">
              <Link to="/auth/login" className="font-medium text-gray-900">
                Back to login
              </Link>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <Input
              label="Account email"
              type="email"
              name="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              leftIcon={<FiMail />}
              error={error}
              required
              disabled={loading}
            />
            <Button fullWidth type="submit" disabled={loading}>
              {loading ? 'Sending...' : 'Send reset link'}
            </Button>
            <p className="text-center text-xs text-gray-500">
              We'll send you a link to reset your password.
            </p>
            <div className="text-center text-xs">
              <Link to="/auth/login" className="font-medium text-gray-900">
                Back to login
              </Link>
            </div>
          </form>
        )}
      </Card>
    </Screen>
  );
}


